// performance.component.ts

import { Component, OnInit } from "@angular/core";
import { PerformanceReport } from "./reporting.models";
import { ReportingService } from "./reporting.service";
import { ExcelService } from "../../../services/Excel.service";
import { StoreService } from "../../../services/store.service";

@Component({
  selector: "app-performance",
  templateUrl: "./performance.component.html",
  styleUrls: ["./performance.component.css"],
})
export class PerformanceComponent implements OnInit {
  msgs = [];
  displaychargement = false;

  listAnnee: any[] = [];
  listMois: any[] = [];
  selectedAnnee: string = null;
  selectedMois: string = null;

  distributors = [];
  selectedDistributor: string = null;
  isUserPartenaire = false;

  items: any[] = [];
  reportData: PerformanceReport = null;

  // KPIs
  totalPurchased = 0;
  totalActivated = 0;
  totalExpired = 0;
  activationRate = 0;
  currentStock = 0;

  // Month-specific KPIs
  monthlyPurchased = 0;
  previousStockBeforeMonth = 0;
  monthlyActivated = 0;
  sameMonthActivated = 0;
  sameMonthActivationRate = 0;
  activationRateMonth = 0;

  // Chart data
  dataBar: any = { labels: [], datasets: [] };
  dataPie: any = { labels: [], datasets: [] };

  constructor(
    private api: ReportingService,
    private excelService: ExcelService,
    private store: StoreService
  ) {}

  ngOnInit() {
    this.isUserPartenaire = this.store.isUserPartenaire;
    this.initDropdowns();
    this.loadDistributors();

    const now = new Date();
    this.selectedAnnee = now.getFullYear().toString();
    this.selectedMois = (now.getMonth() + 1).toString().padStart(2, "0");
  }

  initDropdowns() {
    const currentYear = new Date().getFullYear();
    for (let i = currentYear - 10; i <= currentYear; i++) {
      this.listAnnee.push({ label: i.toString(), value: i.toString() });
    }

    const months = [
      { label: "Tous", value: "00" },
      { label: "Janvier", value: "01" },
      { label: "Février", value: "02" },
      { label: "Mars", value: "03" },
      { label: "Avril", value: "04" },
      { label: "Mai", value: "05" },
      { label: "Juin", value: "06" },
      { label: "Juillet", value: "07" },
      { label: "Août", value: "08" },
      { label: "Septembre", value: "09" },
      { label: "Octobre", value: "10" },
      { label: "Novembre", value: "11" },
      { label: "Décembre", value: "12" },
    ];

    this.listAnnee.unshift({ label: "Tous", value: "0000" });
    this.listMois = [...months];
  }

  loadDistributors() {
    if (this.isUserPartenaire) {
      this.selectedDistributor = this.store.currentNomPDG;
      this.distributors = [
        { label: this.store.currentNomPDG, value: this.store.currentNomPDG },
      ];
      return;
    }

    this.api.getDistributors().subscribe((r) => {
      // Use login (nom_pdg) as the value — maps to AGENT_CHARGEUR in kaabu DB
      this.distributors = r.map((x) => ({ label: x.login, value: x.login }));
    });
  }

  async rechercher() {
    if (!this.selectedAnnee || !this.selectedMois) {
      this.msgs = [
        {
          severity: "error",
          summary: "Erreur",
          detail: "Veuillez sélectionner année et mois",
        },
      ];
      return;
    }

    this.displaychargement = true;

    try {
      const period = this.selectedAnnee + this.selectedMois;
      const distributor = this.isUserPartenaire
        ? this.store.currentNomPDG
        : this.selectedDistributor || "ALL";

      // Call the new performance endpoint (kaabu DB via distri-api)
      this.reportData = await this.api
        .getPerformanceReport(distributor, period)
        .toPromise();

      console.log("Performance Data:", this.reportData);

      // Map detail rows for the table
      this.items = (this.reportData.details || []).map((d) => ({
        msisdn: d.msisdn || "",
        iccid: d.iccid || "",
        distributeur: d.distributeur || "",
        pointVente: d.pointVente || "",
        statut: this.formatStatut(d.statut),
        dateAcquisition: d.dateAcquisition || "",
        dateActivation: d.dateActivation || "",
        type: d.type || "",
      }));

      // KPIs from summary
      const s = this.reportData.summary || ({} as any);
      this.totalPurchased = s.totalPurchased || 0;
      this.totalActivated = s.totalActivated || 0;
      this.totalExpired = s.totalExpired || 0;
      this.currentStock = s.currentStock || 0;
      this.activationRate =
        this.totalPurchased > 0
          ? Math.round((this.totalActivated / this.totalPurchased) * 100)
          : 0;

      // Month-specific KPIs (only meaningful when a specific month is selected)
      // Use backend totals as \"mois sélectionné\" metrics (already filtered by year/month)
      this.monthlyPurchased = s.totalPurchased || s.monthlyPurchased || 0;
      this.monthlyActivated = s.totalActivated || s.monthlyActivated || 0;
      this.previousStockBeforeMonth = s.previousStockBeforeMonth || 0;
      this.sameMonthActivated = s.sameMonthActivated || 0;
      this.sameMonthActivationRate = s.sameMonthActivationRate || 0;
      // Activation rate for selected month: rely on backend value when present
      this.activationRateMonth =
        s.activationRate != null
          ? Math.round(s.activationRate)
          : this.monthlyPurchased > 0
          ? Math.round((this.monthlyActivated / this.monthlyPurchased) * 100)
          : 0;

      this.buildCharts();

      this.msgs = [
        {
          severity: "success",
          summary: "Succès",
          detail: `${this.items.length} enregistrements trouvés`,
        },
      ];
    } catch (err) {
      console.error("Error:", err);
      this.msgs = [
        {
          severity: "error",
          summary: "Erreur",
          detail: "Impossible de charger les données",
        },
      ];
    }

    this.displaychargement = false;
  }

  formatStatut(statut: string): string {
    switch (statut) {
      case "EN_STOCK":
        return "En stock";
      case "ACTIVE":
        return "Activé";
      case "EXPIRE":
        return "Expiré";
      default:
        return statut || "";
    }
  }

  buildCharts() {
    this.dataBar = {
      labels: ["Achetées", "Activées", "Expirées"],
      datasets: [
        {
          label: "SIM",
          backgroundColor: ["#42A5F5", "#00A056", "#FF6384"],
          data: [this.totalPurchased, this.totalActivated, this.totalExpired],
        },
      ],
    };

    this.dataPie = {
      labels: ["Activées", "En stock", "Expirées"],
      datasets: [
        {
          data: [
            this.totalActivated,
            this.totalPurchased - this.totalActivated - this.totalExpired,
            this.totalExpired,
          ],
          backgroundColor: ["#00A056", "#42A5F5", "#FF6384"],
          hoverBackgroundColor: ["#00A056", "#42A5F5", "#FF6384"],
        },
      ],
    };
  }

  exporter() {
    const data = this.items.map((item) => ({
      MSISDN: item.msisdn,
      ICCID: item.iccid,
      Distributeur: item.distributeur,
      Statut: item.statut,
      "Date acquisition": item.dateAcquisition,
      "Date activation": item.dateActivation,
      Type: item.type,
    }));
    this.excelService.exportAsExcelFile(data, "performance_distributeur");
  }
}
